import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { distinctUntilChanged, throttleTime } from 'rxjs';
import * as CryptoJS from 'crypto-js';

@Injectable({
    providedIn: 'root'
})
export class LocalstorageService {
    private readonly PREFIX = 'formState_';
    private readonly SECRET_KEY = 'ISO45001_SECRET_KEY_SECURE_V1'; // Clave para cifrado simétrico

    constructor() { }

    private encrypt(txt: string): string {
        return CryptoJS.AES.encrypt(txt, this.SECRET_KEY).toString();
    }

    private decrypt(txtToDecrypt: string): string {
        try {
            const bytes = CryptoJS.AES.decrypt(txtToDecrypt, this.SECRET_KEY);
            return bytes.toString(CryptoJS.enc.Utf8);
        } catch (e) {
            console.error('Error decrypting data', e);
            return '';
        }
    }

    // ==========================================
    // Core Encrypted Storage Methods (NATIVE API COMPATIBLE - STRING ONLY)
    // ==========================================

    setItem(key: string, value: any): void {
        const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
        const encrypted = this.encrypt(stringValue);
        try {
            sessionStorage.setItem(key, encrypted);
        } catch (e) {
            if (e instanceof DOMException && (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED')) {
                // Los borradores de formularios (con firmas en base64) son la causa
                // más probable de llenar el storage; son descartables, así que se
                // liberan primero y se reintenta el guardado real.
                this.clearAllFormStates();
                try {
                    sessionStorage.setItem(key, encrypted);
                } catch (error_) {
                    this.logStorageUsage(key);
                    throw error_;
                }
            } else {
                throw e;
            }
        }
    }

    private logStorageUsage(failedKey: string): void {
        const sizes = Object.keys(sessionStorage)
            .map((k) => ({ key: k, kb: Math.round((sessionStorage.getItem(k)?.length ?? 0) / 1024) }))
            .sort((a, b) => b.kb - a.kb);
        const total = sizes.reduce((sum, s) => sum + s.kb, 0);
        const detail = sizes.slice(0, 15).map((s) => `  ${s.kb}KB  ${s.key}`).join('\n');
        console.error(
            `sessionStorage lleno al guardar '${failedKey}' (limpiar borradores no fue suficiente). ` +
            `Uso total ~${total}KB de ${sizes.length} claves.\n${detail}`
        );
    }

    getItem(key: string): any {
        const item = sessionStorage.getItem(key);
        if (!item) {
            return null;
        }
        // setItem siempre cifra, así que cualquier valor sin el prefijo de
        // CryptoJS (o que no descifre) se considera manipulado/corrupto y se descarta.
        if (!item.startsWith('U2FsdGVkX1')) {
            sessionStorage.removeItem(key);
            return null;
        }
        const decrypted = this.decrypt(item);
        if (!decrypted) {
            sessionStorage.removeItem(key);
            return null;
        }
        try {
            return JSON.parse(decrypted);
        } catch (e) {
            return decrypted;
        }
    }

    removeItem(key: string): void {
        sessionStorage.removeItem(key);
    }

    setSessionItem(key: string, value: string): void {
        this.setItem(key, value);
    }

    getSessionItem(key: string): any {
        return this.getItem(key);
    }

    removeSessionItem(key: string): void {
        this.removeItem(key);
    }


    // ==========================================
    // Legacy / Specific Methods
    // ==========================================

    guardarCliente(cliente: any): void {
        this.setItem('clienteISO', JSON.stringify(cliente));
    }

    obtenerCliente(): any {
        return this.getItem('clienteISO');
    }

    obtenerItem(key: string): any {
        return this.getItem(key);
    }

    obtenerEmail(): string {
        const user = this.getItem('userData');
        return user?.usuario?.str_usuario_email || '';
    }

    obtenerIdUsuarioSistemaCentro(): number {
        const user = this.getItem('userData');
        return user?.usuario?.int_usuario_sistema_centro_id ?? user?.sistema_id ?? 0;
    }

    obtenerCargoUsuario(): string {
        const data = this.getItem('cargo');
        return data || '';
    }

    initializeFormState(form: FormGroup, formKey: string) {
        this.loadFormState(form, formKey);
        this.setupAutosave(form, formKey);
    }

    private setupAutosave(form: FormGroup, formKey: string) {
        form.valueChanges.pipe(
            throttleTime(1000),
            distinctUntilChanged()
        ).subscribe(() => {
            this.saveFormState(form, formKey);
        });
    }

    saveFormState(form: FormGroup, formKey: string) {
        const formData = this.serializeFormData(form);
        try {
            this.setItem(`${this.PREFIX}${formKey}`, JSON.stringify(formData));
        } catch (e) {
            console.error('Error saving form state:', e);
        }
    }

    loadFormState(form: FormGroup, formKey: string) {
        const parsedData = this.getItem(`${this.PREFIX}${formKey}`);
        if (parsedData && parsedData.value) {
            try {
                this.patchFormData(form, parsedData);
            } catch (e) {
                console.error('Error loading form state:', e);
            }
        }
    }

    clearFormState(formKey: string) {
        this.removeItem(`${this.PREFIX}${formKey}`);
    }

    private serializeFormData(form: FormGroup): any {
        return {
            value: form.getRawValue(),
            controlsState: this.getControlsState(form),
        };
    }

    private getControlsState(form: FormGroup): any {
        return Object.keys(form.controls).reduce((acc: { [key: string]: any }, key) => {
            const control = form.get(key);
            if (control instanceof FormGroup) {
                acc[key] = this.getControlsState(control);
            } else {
                acc[key] = {
                    dirty: control?.dirty,
                    touched: control?.touched,
                    status: control?.status,
                    disabled: control?.disabled,
                };
            }
            return acc;
        }, {});
    }

    private patchFormData(form: FormGroup, data: any) {
        form.patchValue(data.value, { emitEvent: false });
        this.restoreControlsState(form, data.controlsState);
    }

    private restoreControlsState(form: FormGroup, controlsState: any) {
        Object.keys(controlsState).forEach((key) => {
            const control = form.get(key);
            if (control) {
                if (control instanceof FormGroup) {
                    this.restoreControlsState(control, controlsState[key]);
                } else {
                    if (controlsState[key].touched) control.markAsTouched();
                    if (controlsState[key].dirty) control.markAsDirty();
                    if (controlsState[key].disabled) control.disable({ emitEvent: false });
                }
            }
        });
    }

    clearAllFormStates(): void {
        Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith(this.PREFIX)) {
                this.removeItem(key);
            }
        });
    }

    getImplementacion() {
        const idImplementacion = this.getItem('resumen');
        return idImplementacion?.int_implementacion_id;
    }

    getUsuario() {
        return this.getItem('userData');
    }

    getEmpresa() {
        return this.getItem("empresa");
    }

    getIdUser() {
        const user = this.getItem("userData");
        return user?.sistema_id;
    }

    getIdCentro() {
        const centro = this.getItem("centro");
        return centro?.int_centro_id;
    }

    clearLocalStorage() {
        sessionStorage.clear();
    }
}

export const secureStorage = new LocalstorageService();
